import React, {useCallback, useEffect, useState} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// import "dayjs/locale/nb";
import {
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    FormHelperText,
    Grid,
    Stack,
    Typography
} from "@mui/material";
import apiClient from "../../../api/apiClient";
import {EditSchedule, GetCustomText, GetCustomUninviteText, GetCustomUpdateText} from "../../../api/endpoints";
import UserSelect from "../PlanningTab/UserSelect";
import DepartmentUserSelect from "../PlanningTab/DepartmentUserSelect";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";
import CustomTextField from "../PlanningTab/CustomTextField";
import {successNote, toastErrorNotify} from "../../../helpers/toastNotify";

/** @typedef {import('../../../Dtos/ScheduleEditRequestDto').ScheduleEditRequestDto} ScheduleEditRequestDto */

/** @typedef {import('../../../Dtos/ScheduleEditResponseDto').ScheduleEditResponseDto} ScheduleEditResponseDto */
/** @typedef {import('../../../Dtos/ScheduleEditResponseDto').FieldResult} FieldResult */

function toDate(isoString) {
    if (!isoString) return null;
    return isoString.slice(0, 10);
}

function DateField({label, original, value, onChange, minDate, disablePast}) {
    const changed = value?.format("YYYY-MM-DD") !== original;
    const fmt = d => d ? dayjs(d).format("DD.MM.YYYY") : "Ingen";

    const today = dayjs();
    const isPast = value?.isBefore(today, "day");
    return (
        <DesktopDatePicker
            label={label}
            value={isPast ? null : value}
            onChange={onChange}
            inputFormat="DD.MM.YYYY"
            disablePast={disablePast}
            minDate={minDate}
            defaultCalendarMonth={isPast ? today : undefined}
            renderInput={({InputProps, inputRef}) => (
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    label={label}
                    value=""
                    InputProps={{
                        ...InputProps,
                        readOnly: true,
                        startAdornment: (
                            <span style={{fontSize: 14, whiteSpace: "nowrap"}}>
                                {changed && (
                                    <span style={{textDecoration: "line-through", color: "#bdbdbd", marginRight: 6}}>
                                        {fmt(original)}
                                    </span>
                                )}
                                <span>{fmt(value)}</span>
                            </span>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            ...(changed && {"& fieldset": {borderColor: "#EF9F27"}})
                        }
                    }}
                />
            )}
        />
    );
}

function EditScheduleModal({open, onClose, onCancel, onDataUpdated, schedules}) {
    const [loading, setLoading] = useState(false);

    const getInitialUsers = () => schedules.map(s => ({id: s.endUserId, name: s.endUserName}));

    const [course] = useState(schedules[0]);
    const [groupId] = useState(course.groupId);
    const [courseName] = useState(course.courseName);
    const [originalStart, setOriginalStart] = useState(toDate(course?.startDateTime));
    const [originalEnd, setOriginalEnd] = useState(toDate(course?.endDateTime));
    const [userObjects, setUserObjects] = useState(getInitialUsers());
    const [originalEndUsers, setOriginalEndUsers] = useState(schedules.map(user => user.endUserId));

    const [editedStart, setEditedStart] = useState(dayjs(originalStart));
    const [editedEnd, setEditedEnd] = useState(dayjs(originalEnd));

    const role = localStorage.getItem("roles");
    const roleFromLocal = JSON.parse(role);
    const [userValue, setUserValue] = useState(null);
    const [formSubmit, setFormSubmit] = useState(false);
    const [reset, setReset] = useState(false);

    // Toggles for what users should be informed about what changes.
    const [smsValue, setSmsValue] = useState(false);
    const [emailValue, setEmailValue] = useState(true);
    const [informRemovedUsers, setInformRemovedUsers] = useState(true);
    const [informInvitedUsers, setInformInvitedUsers] = useState(true);
    const [informAboutDateChange, setInformAboutDateChange] = useState(true);

    const [displayInformRemovedUsersCheckbox, setDisplayInformRemovedUsersCheckbox] = useState(false);
    const [displayInformAddedUsersCheckbox, setDisplayInformAddedUsersCheckbox] = useState(false);
    const [displayInformDateChangeCheckbox, setDisplayInformDateChangeCheckbox] = useState(false);

    const [customInvitationText, setCustomInvitationText] = useState("");
    const [customUninvitationText, setCustomUninvitationText] = useState("");
    const [customUpdateText, setCustomUpdateText] = useState("");
    const [isCustomTextEditing, setIsCustomTextEditing] = useState(false);
    const [usersTouched, setUsersTouched] = useState(false);
    const [error, setError] = useState(false);

    const getCustomInvitationText = useCallback(async () => {
        const params = {
            courseId: course.courseId,
        };
        try {
            console.log(params)
            const response = await apiClient.post(GetCustomText, params);
            console.log(response)

            const message = response?.data?.data?.message;
            if (!message) {
                toastErrorNotify("Klarte ikke hente kursinforamsjon. Vennligst velg kurset på nytt.")
                return;
            }
            setCustomInvitationText(response.data.data.message);
        } catch (e) {
            console.log(e?.response.data);
        }
    }, [course]);

    const getCustomUninviteText = useCallback(async () => {
        const params = {
            courseId: course.courseId,
        };
        try {
            const response = await apiClient.post(GetCustomUninviteText, params);

            const message = response?.data?.data?.message;
            if (!message) {
                toastErrorNotify("Klarte ikke hente kursinforamsjon. Vennligst velg kurset på nytt.")
                return;
            }
            setCustomUninvitationText(response.data.data.message);
        } catch (e) {
            console.log(e?.response?.data);
        }
    }, [course]);

    const getCustomUpdateText = useCallback(async () => {
        const params = {
            courseId: course.courseId,
            startDate: editedStart.format("YYYY-MM-DD"),
            endDate: editedEnd.format("YYYY-MM-DD"),
        };
        try {
            const response = await apiClient.post(GetCustomUpdateText, params);

            const message = response?.data?.data?.message;
            if (!message) {
                toastErrorNotify("Klarte ikke hente kursinforamsjon. Vennligst velg kurset på nytt.")
                return;
            }
            setCustomUpdateText(response.data.data.message);
        } catch (e) {
            console.log(e?.response.data);
        }
    }, [course, editedStart, editedEnd]);

    useEffect(() => {
        const current = schedules[0];
        const start = toDate(current?.startDateTime);
        const end = toDate(current?.endDateTime);

        setOriginalStart(start);
        setOriginalEnd(end);
        setEditedStart(dayjs(start));
        setEditedEnd(dayjs(end));

        setOriginalEndUsers(schedules.map(user => user.endUserId));

        setDisplayInformRemovedUsersCheckbox(false);
        setDisplayInformAddedUsersCheckbox(false);
        setDisplayInformDateChangeCheckbox(false);
        setUsersTouched(false);
    }, [open]);

    useEffect(() => {
        setError(!userValue || userValue.length === 0);
    }, [open, editedStart, editedEnd, userValue]);

    // const [courseEditResponse, setCourseEditResponse] = useState(null);

    const isUpperAdmin = roleFromLocal.some((role) =>
        ["org.admin.super", "org.admin.assistant", "org.admin.course"].includes(
            role
        )
    );

    const handleConfirm = async () => {
        setLoading(true);
        try {
            /** @type {ScheduleEditRequestDto} */
            const params = {
                groupId: groupId,
                startDate: editedStart.format("YYYY-MM-DD"),
                endDate: editedEnd.format("YYYY-MM-DD"),
                invitationMessage: customInvitationText,
                uninviteMessage: customUninvitationText,
                updateMessage: customUpdateText,
                endUserId: userValue,
                sendInviteToNewUsers: informInvitedUsers && displayInformAddedUsersCheckbox,
                sendNotificationAboutChangedDates: informAboutDateChange && displayInformDateChangeCheckbox,
                sendNotificationToRemovedUsers: informRemovedUsers && displayInformRemovedUsersCheckbox
            };
            console.log(params);
            const response = (await apiClient.post(EditSchedule, params)).data.data;
            handleUpdateResponse(response);
            // On success: invoke onDataUpdated ({new Data.})
        } catch (error) {
            toastErrorNotify(error.message);
        } finally {
            setLoading(false);
        }
    };


    const handleUpdateResponse = (/** @type {ScheduleEditResponseDto} */ response) => {
        // setCourseEditResponse(response);
        const fields = [
            response.startDate,
            response.endDate,
            response.endUsersId,
            response.invitationMessage,
            response.uninviteMessage,
            response.updateMessage
        ];
        
        const errors = fields
            .filter(field => !field.isValid)
            .reduce((acc, field) => acc.concat(field.errors), []);

        if (errors.length === 0) {
            const formattedStart = editedStart.format("YYYY-MM-DD");
            const formattedEnd = editedEnd.format("YYYY-MM-DD");

            setOriginalStart(formattedStart);
            setOriginalEnd(formattedEnd);
            setOriginalEndUsers(userValue);

            setDisplayInformRemovedUsersCheckbox(false);
            setDisplayInformAddedUsersCheckbox(false);
            setDisplayInformDateChangeCheckbox(false);

            onDataUpdated?.({
                startDateTime: formattedStart,
                endDateTime: formattedEnd,
                endUsers: userObjects
            });

            successNote("Kurset er oppdatert!");
        } else {
            console.log("errors", errors);
            errors.forEach(error => toastErrorNotify(error));
        }
    };

    const handleUserSelect = (selectedBox) => {
        const data = selectedBox.map((item) => item.id);
        setUserValue(data);
        setUserObjects(selectedBox); // keep full objects
        setUsersTouched(true);

        const hasAddedUsers = data.some(id => !originalEndUsers.includes(id));
        const hasRemovedUsers = originalEndUsers.some(id => !data.includes(id));
        setDisplayInformAddedUsersCheckbox(hasAddedUsers);
        setDisplayInformRemovedUsersCheckbox(hasRemovedUsers);
    };


    const handleStartDateChange = (newValue) => {
        const adjustedEnd = editedEnd.isBefore(newValue) ? newValue.add(1, "day") : editedEnd;
        setEditedStart(newValue);
        setEditedEnd(adjustedEnd);
        const startChanged = newValue?.format("YYYY-MM-DD") !== originalStart;
        const endChanged = adjustedEnd?.format("YYYY-MM-DD") !== originalEnd;
        setDisplayInformDateChangeCheckbox(startChanged || endChanged);
    };

    const handleEndDateChange = (newValue) => {
        setEditedEnd(newValue);
        const startChanged = editedStart?.format("YYYY-MM-DD") !== originalStart;
        const endChanged = newValue?.format("YYYY-MM-DD") !== originalEnd;
        setDisplayInformDateChangeCheckbox(startChanged || endChanged);
    };

    function anyChanges() {
        const startChanged = editedStart?.format("YYYY-MM-DD") !== originalStart;
        const endChanged = editedEnd?.format("YYYY-MM-DD") !== originalEnd;
        const usersChanged = displayInformAddedUsersCheckbox || displayInformRemovedUsersCheckbox;
        return startChanged || endChanged || usersChanged;
    }

    function anyErrors() {
        return usersTouched && (!userValue || userValue.length === 0);
    }

    const handleCheckboxInformRemovedUsers = (e) => {
        setInformRemovedUsers(e.target.checked);
    }

    const handleCheckboxInformAddedUsers = (e) => {
        setInformInvitedUsers(e.target.checked);
    }

    const handleCheckboxInformDateChange = (e) => {
        setInformAboutDateChange(e.target.checked);
    }

    function handleCustomTextEditing() {
        setIsCustomTextEditing(true);
    }

    function handleCustomTextDoneEditing() {
        setIsCustomTextEditing(false);
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
            <Dialog open={open} onClose={onClose} aria-labelledby="edit-schedule-dialog-title" fullWidth={true}
                    maxWidth={"md"}>
                {/*<DialogTitle id="edit-schedule-dialog-title">Rediger kurs</DialogTitle>*/}
                {loading && (
                    <Box sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        backgroundColor: "rgba(255,255,255,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <CircularProgress size={32} sx={{color: "#5177B8"}}/>
                    </Box>
                )}
                <DialogContent>
                    <Typography variant="h6">Rediger kurs</Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{mb: 3}}>{courseName}</Typography>

                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1, minWidth: 320}}>
                        {/*<Typography variant="subtitle2" color="text.secondary">Endre kursdeltaker.</Typography>*/}
                        <Grid item xs={12} sx={{mb: 3}}>
                            {isUpperAdmin ? (
                                <UserSelect
                                    handleUserSelect={handleUserSelect}
                                    formSubmit={formSubmit}
                                    reset={reset}
                                    setReset={setReset}
                                    initialUserValue={getInitialUsers()}
                                    error={anyErrors()}
                                />
                            ) : (
                                <DepartmentUserSelect
                                    handleUserSelect={handleUserSelect}
                                    formSubmit={formSubmit}
                                    reset={reset}
                                    setReset={setReset}
                                    initialUserValue={getInitialUsers()}
                                    error={anyErrors()}
                                />
                            )}

                            {displayInformRemovedUsersCheckbox && !anyErrors() && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={informRemovedUsers}
                                                onChange={handleCheckboxInformRemovedUsers}
                                            />
                                        }
                                        label="Informer fjernede brukere"
                                    />
                                    {informRemovedUsers && (
                                        <CustomTextField
                                            value={customUninvitationText}
                                            customText={customUninvitationText}
                                            setCustomText={setCustomUninvitationText}
                                            onCustomTextEditing={handleCustomTextEditing}
                                            onCustomTextDoneEditing={handleCustomTextDoneEditing}
                                            getCustomText={getCustomUninviteText}
                                            courseValue={course.courseId}>
                                        </CustomTextField>
                                    )}
                                </>
                            )}

                            {displayInformAddedUsersCheckbox && !anyErrors() && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={informInvitedUsers}
                                                onChange={handleCheckboxInformAddedUsers}
                                            />
                                        }
                                        label="Informer inviterte brukere"/>

                                    {informInvitedUsers && (
                                        <CustomTextField
                                            value={customInvitationText}
                                            customText={customInvitationText}
                                            setCustomText={setCustomInvitationText}
                                            onCustomTextEditing={handleCustomTextEditing}
                                            onCustomTextDoneEditing={handleCustomTextDoneEditing}
                                            getCustomText={getCustomInvitationText}
                                            courseValue={course.courseId}>
                                        </CustomTextField>
                                    )}
                                </>
                            )}

                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2}>
                                <DateField
                                    label="Startdato"
                                    original={originalStart}
                                    value={editedStart}
                                    onChange={handleStartDateChange}
                                    disablePast
                                />
                                <DateField
                                    label="Sluttdato"
                                    original={originalEnd}
                                    value={editedEnd}
                                    onChange={handleEndDateChange}
                                    disablePast
                                    minDate={editedStart}
                                />
                            </Stack>

                            {displayInformDateChangeCheckbox && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={informAboutDateChange}
                                                onChange={handleCheckboxInformDateChange}
                                            />
                                        }
                                        label="Informer brukere om datoendringer"/>

                                    {informAboutDateChange && (
                                        <CustomTextField
                                            value={customUpdateText}
                                            customText={customUpdateText}
                                            setCustomText={setCustomUpdateText}
                                            onCustomTextEditing={handleCustomTextEditing}
                                            onCustomTextDoneEditing={handleCustomTextDoneEditing}
                                            getCustomText={getCustomUpdateText}
                                            courseValue={course.courseId}>
                                        </CustomTextField>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Box>
                    {formSubmit && anyErrors() && (
                        <FormHelperText sx={{ml: 1}} error>
                            Vennligst velg minst ett alternativ
                        </FormHelperText>
                    )}
                </DialogContent>
                <DialogActions sx={{m: 1}}>
                    <Button variant="contained" className="PrimaryButton" onClick={onCancel}>
                        {anyChanges() ? "Avbryt" : "Lukk"}
                    </Button>
                    <Button disabled={!anyChanges() || anyErrors()} variant="contained" color="success"
                            onClick={handleConfirm}
                            autoFocus>
                        Lagre
                    </Button>


                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}

function EditTextField({original, value, label, onChange, /** @type {FieldResult} */ fieldResult = null}) {
    const changed = value !== original;
    return (
        <Box>
            {changed && (
                <Typography variant="caption" sx={{color: "#bdbdbd", textDecoration: "line-through"}}>
                    {original}
                </Typography>
            )}
            <TextField
                label={label}
                value={value}
                onChange={e => onChange(e.target.value)}
                fullWidth
                multiline
                sx={{
                    "& .MuiOutlinedInput-root": {
                        ...(changed && {"& fieldset": {borderColor: "#EF9F27"}})
                    }
                }}
                error={Boolean(fieldResult && !fieldResult.isValid)}
                helperText={fieldResult?.errors?.map((err, i) => (
                    <span key={i} style={{display: "block"}}>{err}</span>
                ))}
            />
        </Box>
    );
}

export default EditScheduleModal;